import React, { useState, useEffect, useMemo, useRef } from 'react';
import { offersAPI, priceDynamicsAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { useChartColors } from '../contexts/ThemeContext';
import { PageHeader, Switch, Alert, Tile, Icon, saveBlob } from '../ui';
import {
    BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer
} from 'recharts';

// Розбіжна пара: синє — ціна знизилась, помаранчеве — зросла (--down / --up
// у темі). Не зелене/червоне: зелений в інших розділах означає «ми дешевші за ринок».
const MAX_DATES = 4;
// Брендів понад сотню — на графіку лише найбільші, решта в таблиці.
const CHART_LIMIT = 20;
// Межі шкали графіка (частки): найменша, що вміщує найбільшу зміну.
const NICE_EXTENTS = [0.005, 0.01, 0.02, 0.03, 0.05, 0.1, 0.2, 0.3, 0.5, 1];

const isoDay = (d) => d.toLocaleDateString('sv-SE');   // РРРР-ММ-ДД у місцевому часі
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return isoDay(d); };

const fmtPrice = (v) => v == null ? '—' : Math.round(v).toLocaleString('uk-UA');
const fmtChange = (v) => v == null ? '—' : `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v * 100).toFixed(2)}%`;
const fmtShare = (v) => v == null ? '—' : `${(v * 100).toFixed(1)}%`;
const changeTint = (v) => (v == null || v === 0) ? undefined : (v > 0 ? 'var(--up-soft)' : 'var(--down-soft)');

const PriceDynamicsPage = () => {
    const [sources, setSources] = useState([]);
    const [source, setSource] = useState('infoshina');
    const [range, setRange] = useState(null);
    const [dates, setDates] = useState([daysAgo(7), daysAgo(1), daysAgo(0)]);
    const [completeOnly, setCompleteOnly] = useState(true);
    // Повернення на сторінку з тими самими датами — одразу з кешу.
    const [data, setData] = useState(() =>
        priceDynamicsAPI.peekSummary('infoshina', [daysAgo(7), daysAgo(1), daysAgo(0)], true) || null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [segment, setSegment] = useState('season');
    const [pairIdx, setPairIdx] = useState(null);
    const [sort, setSort] = useState(null);   // { key, dir }
    const [exporting, setExporting] = useState(false);
    const requestId = useRef(0);
    const firstRun = useRef(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await offersAPI.sources();
                setSources((res.sources || []).filter(s => s.is_active));
            } catch (err) {
                console.error('Failed to load sources:', err);
            }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                setRange(await priceDynamicsAPI.range(source));
            } catch (err) {
                console.error('Failed to load history range:', err);
                setRange(null);
            }
        })();
    }, [source]);

    // Відсортовані унікальні дати — те, що реально піде на сервер.
    const queryDates = useMemo(() => [...new Set(dates.filter(Boolean))].sort(), [dates]);

    useEffect(() => {
        // Номер запиту — і до перевірки дат: відповідь на попередній запит, що
        // ще в дорозі, не повинна перезаписати стан після зміни параметрів.
        const id = ++requestId.current;
        if (queryDates.length < 2) {
            setData(null);
            setLoading(false);
            setError('Обери щонайменше дві різні дати.');
            return;
        }
        // Затримка — щоб не рахувати на кожен клік у календарі; першого
        // відкриття сторінки вона не стосується.
        const delay = firstRun.current ? 0 : 400;
        firstRun.current = false;
        const t = setTimeout(async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await priceDynamicsAPI.summary(source, queryDates, completeOnly);
                // Користувач міг змінити дати, поки рахувалось попереднє.
                if (id !== requestId.current) return;
                setData(res);
                setPairIdx(null);
            } catch (err) {
                if (id !== requestId.current) return;
                setData(null);
                setError(err.response?.data?.detail || 'Не вдалося порахувати динаміку.');
            } finally {
                if (id === requestId.current) setLoading(false);
            }
        }, delay);
        return () => clearTimeout(t);
    }, [source, queryDates, completeOnly]);

    const setDateAt = (i, value) => setDates(prev => prev.map((d, k) => (k === i ? value : d)));
    const removeDate = (i) => setDates(prev => prev.filter((_, k) => k !== i));
    // Нова дата — сьогодні, а якщо вона вже є, то тиждень до найранішої.
    const addDate = () => setDates(prev => {
        const today = daysAgo(0);
        if (!prev.includes(today)) return [...prev, today].sort();
        const earliest = new Date(`${[...prev].sort()[0]}T00:00:00`);   // місцевий час, не UTC
        earliest.setDate(earliest.getDate() - 7);
        return [isoDay(earliest), ...prev].sort();
    });

    const handleExport = async () => {
        setExporting(true);
        try {
            const blob = await priceDynamicsAPI.exportXlsx(source, queryDates, completeOnly);
            saveBlob(blob, `price_dynamics_${source}_${queryDates.join('_')}.xlsx`);
        } catch (err) {
            setError('Не вдалося сформувати файл: ' + (err.message || ''));
        } finally {
            setExporting(false);
        }
    };

    const labels = data?.labels || [];
    const pairs = data?.pairs || [];
    // Графік за замовчуванням — пара дат з найпомітнішою зміною: остання проти
    // першої часто дає 0% (разова акція посередині), і графік виходить порожнім.
    const defaultPair = useMemo(() => {
        if (!data) return 0;
        const med = data.total.median_change;
        return med.reduce((best, v, k) =>
            Math.abs(v ?? 0) > Math.abs(med[best] ?? 0) ? k : best, data.main_pair);
    }, [data]);
    const activePair = pairIdx ?? defaultPair;
    const pairLabel = (k) => pairs[k] ? `${labels[pairs[k][0]]} / ${labels[pairs[k][1]]}` : '';
    const mainLabel = data ? pairLabel(data.main_pair) : '';
    const total = data?.total;
    const rows = useMemo(() => data?.segments?.[segment] || [], [data, segment]);

    const sortedRows = useMemo(() => {
        if (!sort) return rows;
        const value = (r) => {
            const [field, idx] = sort.key.split(':');
            const v = idx != null ? r[field]?.[Number(idx)] : r[field];
            return v ?? (sort.dir === 'asc' ? Infinity : -Infinity);
        };
        return [...rows].sort((a, b) => {
            const va = value(a), vb = value(b);
            if (typeof va === 'string') return sort.dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
            return sort.dir === 'asc' ? va - vb : vb - va;
        });
    }, [rows, sort]);

    const chartRows = useMemo(() => {
        const byCount = segment === 'brand'
            ? [...rows].sort((a, b) => b.count - a.count).slice(0, CHART_LIMIT)
            : rows;
        return byCount
            .filter(r => r.median_change[activePair] != null)
            .map(r => ({ label: r.label, value: r.median_change[activePair], row: r }));
    }, [rows, segment, activePair]);

    const toggleSort = (key) => setSort(prev =>
        prev?.key === key ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: key === 'label' ? 'asc' : 'desc' });

    // Функція, а не компонент: компонент, оголошений усередині, перемонтовувався б
    // на кожен рендер.
    const sortTh = (k, children, align = 'right') => (
        <th key={k} className={`sortable${align === 'right' ? ' r' : ''}`} onClick={() => toggleSort(k)}>
            {children}{sort?.key === k ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : ''}
        </th>
    );

    return (
        <div className="ui-page">
            <PageHeader
                title="Динаміка цін"
                description="Як змінювались ціни конкурента між обраними датами — по сезону, бренду, діаметру
                    та ціновому сегменту. Ціна на дату — остання відома на кінець того дня."
                actions={
                    <button className="ui-btn ui-btn--primary" onClick={handleExport} disabled={exporting || !data}>
                        <Icon name="download" />
                        {exporting ? 'Формую…' : 'Завантажити Excel'}
                    </button>
                }
            />

            {/* ── Параметри зрізу ── */}
            <section className="ui-card">
                <div className="ui-toolbar" style={{ alignItems: 'flex-end', gap: 14 }}>
                    <label className="ui-field">
                        <span>Джерело</span>
                        <select value={source} onChange={e => setSource(e.target.value)}>
                            {(sources.length ? sources : [{ source_key: 'infoshina', title: 'Infoshina' }]).map(s => (
                                <option key={s.source_key} value={s.source_key}>{s.title}</option>
                            ))}
                        </select>
                    </label>

                    {dates.map((d, i) => (
                        <label key={i} className="ui-field">
                            <span>Дата {i + 1}</span>
                            <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <input
                                    type="date"
                                    value={d}
                                    min={range?.min_date || undefined}
                                    max={range?.max_date || undefined}
                                    onChange={e => setDateAt(i, e.target.value)}
                                />
                                {dates.length > 2 && (
                                    <button type="button" onClick={() => removeDate(i)} className="ui-icon-btn ui-icon-btn--sm"
                                            title="Прибрати дату" aria-label={`Прибрати дату ${i + 1}`}>
                                        <Icon name="close" size={16} />
                                    </button>
                                )}
                            </span>
                        </label>
                    ))}
                    {dates.length < MAX_DATES && (
                        <button type="button" onClick={addDate} className="ui-btn">
                            <Icon name="plus" size={18} /> Дата
                        </button>
                    )}

                    <Switch checked={completeOnly} onChange={setCompleteOnly}>
                        Лише товари з ціною на всі дати
                    </Switch>
                </div>
                {range?.min_date && (
                    <div className="muted" style={{ fontSize: 13, marginTop: 12 }}>
                        Історія цін є з {range.min_date}.
                        {!completeOnly && ' Без цього прапорця нові й зниклі товари потрапляють у середні та «найдешевше».'}
                    </div>
                )}
            </section>

            {error && <Alert kind="error">{error}</Alert>}

            {loading && !data ? <LoadingSpinner /> : data && (
                <div style={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s', display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {/* ── Підсумок ── */}
                    <div className="ui-tiles">
                        <Tile label="Товарів у зрізі" value={total.count.toLocaleString('uk-UA')}
                              note={`з ${data.offers_considered.toLocaleString('uk-UA')} у базі`} />
                        <Tile label={`Медіана зміни ${mainLabel}`} value={fmtChange(total.median_change[data.main_pair])} />
                        <Tile label="Подорожчало" value={fmtShare(total.up)} note={mainLabel} />
                        <Tile label="Подешевшало" value={fmtShare(total.down)} note={mainLabel} />
                        <Tile label="Без змін" value={fmtShare(total.same)} note={mainLabel} />
                    </div>

                    {total.count === 0 ? (
                        <div className="ui-empty">
                            На ці дати немає товарів з цінами. Перевір, чи є історія з обраної першої дати
                            {completeOnly && ', або зніми прапорець «Лише товари з ціною на всі дати»'}.
                        </div>
                    ) : (
                        <>
                            {/* ── Сегменти ── */}
                            <div className="ui-toolbar" style={{ gap: 6 }}>
                                {Object.entries(data.segment_titles).map(([key, title]) => (
                                    <button key={key} onClick={() => { setSegment(key); setSort(null); }}
                                            className={`ui-pill${key === segment ? ' is-on' : ''}`}>
                                        {title}
                                    </button>
                                ))}
                            </div>

                            <section className="ui-card">
                                <div className="ui-card-head">
                                    <h3>
                                        Медіана зміни ціни {pairLabel(activePair)}
                                        {segment === 'brand' && rows.length > CHART_LIMIT &&
                                            <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}> · {CHART_LIMIT} найбільших брендів</span>}
                                    </h3>
                                    {pairs.length > 1 && (
                                        <select value={activePair} onChange={e => setPairIdx(Number(e.target.value))}
                                                className="ui-select-pill" aria-label="Пара дат">
                                            {pairs.map((_, k) => <option key={k} value={k}>{pairLabel(k)}</option>)}
                                        </select>
                                    )}
                                </div>
                                <ChangeChart rows={chartRows} labels={labels} />
                                <div className="ui-toolbar muted" style={{ gap: 16, fontSize: 13, marginTop: 8 }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                        <span className="ui-dot" style={{ background: 'var(--down)', borderRadius: 3 }} />ціна знизилась
                                    </span>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                        <span className="ui-dot" style={{ background: 'var(--up)', borderRadius: 3 }} />ціна зросла
                                    </span>
                                </div>
                            </section>

                            <section className="ui-card ui-card--flush">
                                <div className="ui-table-wrap">
                                    <table className="ui-table">
                                        <thead>
                                            <tr>
                                                {sortTh('label', data.segment_titles[segment], 'left')}
                                                {sortTh('count', 'Позицій')}
                                                {labels.map((l, i) => sortTh(`mean:${i}`, `Сер. ціна ${l}`))}
                                                {pairs.map((_, k) => sortTh(`median_change:${k}`, pairLabel(k)))}
                                                {sortTh('up', 'Подорожчало')}
                                                {sortTh('down', 'Подешевшало')}
                                                {labels.map((l, i) => sortTh(`cheapest:${i}`, `Найдешевше ${l}`))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedRows.map(r => <SegmentRow key={r.label} r={r} labels={labels} pairs={pairs} />)}
                                            <SegmentRow r={total} labels={labels} pairs={pairs} isTotal />
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                            <div className="muted" style={{ fontSize: 13, maxWidth: '85ch' }}>
                                Зміна — медіана по товарах, що мали ціну на обидві дати. «Найдешевше» — частка товарів,
                                у яких ціна на цю дату була мінімальною; при рівних цінах зараховується кожній даті,
                                тому сума може перевищувати 100%. Сегменти з менш ніж 5 товарами не показані.
                            </div>

                            <TopSpread items={data.top_spread} labels={labels} />
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

const SegmentRow = ({ r, labels, pairs, isTotal }) => (
    <tr className={isTotal ? 'is-total' : undefined}>
        <td className="nowrap">{r.label}</td>
        <td className="r nowrap">{r.count.toLocaleString('uk-UA')}</td>
        {labels.map((_, i) => <td key={`m${i}`} className="r nowrap">{fmtPrice(r.mean[i])}</td>)}
        {pairs.map((_, k) => (
            <td key={`p${k}`} className="r nowrap" style={{ background: changeTint(r.median_change[k]), fontWeight: 600 }}>
                {fmtChange(r.median_change[k])}
            </td>
        ))}
        <td className="r nowrap">{fmtShare(r.up)}</td>
        <td className="r nowrap">{fmtShare(r.down)}</td>
        {labels.map((_, i) => <td key={`c${i}`} className="r nowrap">{fmtShare(r.cheapest[i])}</td>)}
    </tr>
);

const ChangeChart = ({ rows, labels }) => {
    const colors = useChartColors();
    if (!rows.length) {
        return <div className="muted" style={{ padding: 24 }}>Немає товарів з ціною на обидві дати.</div>;
    }
    // Симетрична шкала з круглими поділками: нуль посередині, щоб знак зміни
    // читався з першого погляду. Поділки задаємо самі — автоматичні recharts
    // на «некруглому» діапазоні зсували підпис 0% відносно лінії нуля.
    const maxAbs = Math.max(...rows.map(r => Math.abs(r.value))) * 1.1;
    const extent = NICE_EXTENTS.find(s => s >= maxAbs) || Math.ceil(maxAbs);
    const ticks = [-extent, -extent / 2, 0, extent / 2, extent];
    const height = rows.length * 32 + 40;
    return (
        <div style={{ width: '100%', height }}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 4 }}>
                    <CartesianGrid horizontal={false} stroke={colors.line} />
                    <XAxis type="number" domain={[-extent, extent]} ticks={ticks} allowDataOverflow
                           tickFormatter={v => `${+(v * 100).toFixed(2)}%`}
                           tick={{ fontSize: 12, fill: colors.text3 }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="label" width={190} interval={0}
                           tick={{ fontSize: 13, fill: colors.text2 }} axisLine={false} tickLine={false} />
                    <ReferenceLine x={0} stroke={colors.text3} />
                    <Tooltip cursor={{ fill: colors.surface2 }} content={<ChartTooltip labels={labels} />} />
                    <Bar dataKey="value" barSize={18} radius={[0, 6, 6, 0]} isAnimationActive={false}>
                        {rows.map(r => <Cell key={r.label} fill={r.value < 0 ? colors.down : colors.up} />)}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

const ChartTooltip = ({ active, payload, labels }) => {
    if (!active || !payload?.length) return null;
    const { label, value, row } = payload[0].payload;
    return (
        <div className="own-tooltip">
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
            <div>Медіана зміни: <b>{fmtChange(value)}</b></div>
            <div className="muted-2">Позицій: {row.count.toLocaleString('uk-UA')}</div>
            {labels.map((l, i) => (
                <div key={i} className="muted-2">Сер. ціна {l}: {fmtPrice(row.mean[i])}</div>
            ))}
        </div>
    );
};

const TopSpread = ({ items, labels }) => {
    if (!items?.length) return null;
    return (
        <section className="ui-card ui-card--flush">
            <div className="ui-card-head" style={{ display: 'block' }}>
                <h3 style={{ margin: 0 }}>Найбільший розкид цін між датами</h3>
                <p className="muted" style={{ margin: '6px 0 0', fontSize: 13 }}>
                    Кандидати на перевірку: тут ціна між датами змінювалась найсильніше. Повний список — в Excel.
                </p>
            </div>
            <div className="ui-table-wrap" style={{ marginTop: 8 }}>
                <table className="ui-table">
                    <thead>
                        <tr>
                            <th>Товар</th>
                            <th>Сезон</th>
                            {labels.map((l, i) => <th key={i} className="r">{l}</th>)}
                            <th className="r">Розкид</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(it => (
                            <tr key={it.id}>
                                <td>
                                    {it.url ? <a href={it.url} target="_blank" rel="noreferrer" style={{ color: 'var(--text)', fontWeight: 600 }}>{it.name}</a>
                                            : <span style={{ fontWeight: 600 }}>{it.name}</span>}
                                </td>
                                <td className="nowrap muted-2">{it.season_label}</td>
                                {it.prices.map((p, i) => (
                                    <td key={i} className="r nowrap" style={{ fontWeight: p != null && p === it.min_price ? 700 : 400 }}>
                                        {fmtPrice(p)}
                                    </td>
                                ))}
                                <td className="r nowrap">{fmtShare(it.spread_pct)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

export default PriceDynamicsPage;
